import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Autocomplete,
  Typography,
  IconButton,
  InputAdornment,
  Box,
  Divider,
  keyframes,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MapIcon from '@mui/icons-material/Map';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PinDropIcon from '@mui/icons-material/PinDrop';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Specification Section Icons
import ShieldCheckIcon from '@mui/icons-material/GppGoodOutlined';

// Form Input Field Icons
import ApartmentIcon from '@mui/icons-material/Apartment';
import TagIcon from '@mui/icons-material/Tag';
import SignpostIcon from '@mui/icons-material/Signpost';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MarkAsUnreadIcon from '@mui/icons-material/MarkAsUnread';
import PublicIcon from '@mui/icons-material/Public';

import { debounce } from '@mui/material';
import WalkScoreModal from './Walkscoremodal';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { WarningIcon } from 'component/BuyersMarketplace/MakeOffers';
import { getMarkerIcon } from './Mapelements';
import InfoIcon from '@mui/icons-material/Info';

const smoothPop = keyframes`
  0% { transform: scale(0.99); filter: brightness(0.95); }
  50% { transform: scale(1.01); filter: brightness(1.02); }
  100% { transform: scale(1); filter: brightness(1); }
`;

const AUSTRALIAN_STATES = [
  'Victoria (VIC)',
  'New South Wales (NSW)',
  'Queensland (QLD)',
  'Western Australia (WA)',
  'South Australia (SA)',
  'Tasmania (TAS)',
  'Australian Capital Territory (ACT)',
  'Northern Territory (NT)',
];

export default function AddressModal({
  open,
  onClose,
  streetType,
  onSave,
  initialData,
  isneedWalkscore,
  setIsAddressModalOpen,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [addressDetails, setAddressDetails] = useState({
    unitNo: '',
    streetNumber: '',
    streetName: '',
    selectedStreetType: '',
    selectedStreetTypeabbrev: '',
    town: '',
    postCode: '',
    selectedState: '',
    country: 'Australia',
    latitude: null,
    longitude: null,
  });

  const [inputValues, setInputValues] = useState({
    unitNo: '',
    streetNumber: '',
    streetName: '',
    town: '',
    postCode: '',
  });

  const [options, setOptions] = useState({
    unitNo: [],
    streetNumber: [],
    streetName: [],
    town: [],
    postCode: [],
  });
  const [loadingField, setLoadingField] = useState(null);

  const [isVerified, setIsVerified] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [isManuallyEdited, setIsManuallyEdited] = useState(false);
  const [triggerAnimate, setTriggerAnimate] = useState(false);
  const [walkScoreModalOpen, setWalkScoreModalOpen] = useState(false);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [showMapView, setShowMapView] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [openManualInfo, setOpenManualInfo] = useState(false);

  // Unit No is ignored as a mandatory verification requirement, Street Number is Mandatory
  const isAddressFullyValid = useMemo(() => {
    return (
      isVerified &&
      !!addressDetails.streetNumber?.trim() && // 👈 Added streetNumber as mandatory
      !!addressDetails.streetName?.trim() &&
      !!addressDetails.selectedStreetType?.trim() &&
      !!addressDetails.town?.trim() &&
      !!addressDetails.selectedState?.trim() &&
      !!addressDetails.postCode?.trim()
    );
  }, [isVerified, addressDetails]);

  const isManualAddressValid = useMemo(() => {
    return (
      addressDetails.streetNumber.trim() &&
      addressDetails.streetName.trim() &&
      addressDetails.selectedStreetType.trim() &&
      addressDetails.town.trim() &&
      addressDetails.selectedState.trim() &&
      addressDetails.postCode.trim()
    );
  }, [addressDetails]);

  useEffect(() => {
    if (open) {
      setIsChanged(false);
      setIsManualEntry(false);
      const initial = {
        unitNo: initialData?.unitNo || '',
        streetNumber: initialData?.streetNumber || '',
        streetName: initialData?.streetName || '',
        selectedStreetType: initialData?.selectedStreetType || '',
        selectedStreetTypeabbrev: initialData?.selectedStreetTypeabbrev || '',
        town: initialData?.town || '',
        postCode: initialData?.postCode || '',
        selectedState: initialData?.selectedState
          ? initialData.selectedState
          : '',
        country: 'Australia',
        latitude: initialData?.latitude || null,
        longitude: initialData?.longitude || null,
      };
      setSelectedMarker({
        lat: initialData?.latitude || null,
        lng: initialData?.longitude || null,
      });
      setAddressDetails(initial);
      setInputValues({
        unitNo: initial.unitNo,
        streetNumber: initial.streetNumber,
        streetName: initial.streetName,
        town: initial.town,
        postCode: initial.postCode,
      });

      // Update verification check condition here as well
      setIsVerified(
        !!initial.streetNumber && // 👈 Ensure initial data checks for street number too
          !!initial.streetName &&
          !!initial.selectedStreetType &&
          !!initial.town &&
          !!initial.selectedState &&
          !!initial.postCode
      );
      setIsManuallyEdited(false);
    }
  }, [open, initialData]);

  const fetchPredictions = useMemo(
    () =>
      debounce((request, callback) => {
        if (window.google && window.google.maps && window.google.maps.places) {
          const service = new window.google.maps.places.AutocompleteService();
          service.getPlacePredictions(
            { ...request, componentRestrictions: { country: 'au' } },
            callback
          );
        }
      }, 400),
    []
  );

  const handleManualEntry = () => {
    setIsManualEntry(true);
    setIsVerified(false);
    setIsManuallyEdited(true);
    setAddressDetails((prev) => ({
      ...prev,
    }));
  };

  const validateAddress = () => {
    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();

      const fullAddress = [
        addressDetails.unitNo && `${addressDetails.unitNo}/`,
        addressDetails.streetNumber,
        addressDetails.streetName,
        addressDetails.selectedStreetType,
        addressDetails.town,
        addressDetails.selectedState,
        addressDetails.postCode,
        'Australia',
      ]
        .filter(Boolean)
        .join(' ');

      geocoder.geocode({ address: fullAddress }, (results, status) => {
        if (status !== 'OK' || !results.length) {
          reject('Invalid address');
          return;
        }

        const result = results[0];

        const postcode = result.address_components.find((c) =>
          c.types.includes('postal_code')
        )?.long_name;

        if (postcode !== addressDetails.postCode) {
          reject('Google corrected the postcode.');
          return;
        }

        resolve({
          latitude: result.geometry.location.lat(),
          longitude: result.geometry.location.lng(),
        });
      });
    });
  };

  const handleInputChange = (fieldName, value) => {
    if (fieldName === 'postCode' && isManualEntry) {
      // This strips non-numeric characters if a user manages to paste them
      value = value.replace(/[^0-9]/g, '');
    }

    // 1. Only proceed if the value is genuinely different from what is stored
    if (inputValues[fieldName] === value) {
      return;
    }

    // 2. Update the state
    setInputValues((prev) => ({ ...prev, [fieldName]: value }));

    setAddressDetails((prev) => ({
      ...prev,
      [fieldName]: value,
      latitude: null,
      longitude: null,
    }));

    // 3. Now it is safe to set the change flag
    setIsChanged(true);
    setIsVerified(false);
    setIsManuallyEdited(true);

    if (!value.trim()) {
      setOptions((prev) => ({ ...prev, [fieldName]: [] }));
      return;
    }

    setLoadingField(fieldName);
    fetchPredictions({ input: value }, (results) => {
      setOptions((prev) => ({ ...prev, [fieldName]: results || [] }));
      setLoadingField(null);
    });
  };

  const handleSelectOption = (event, selectedOption, fieldName) => {
    if (!selectedOption) return;

    if (window.google && window.google.maps && window.google.maps.places) {
      const placesService = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );

      placesService.getDetails(
        {
          placeId: selectedOption.place_id,
          fields: ['address_components', 'geometry'],
        },
        (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place.address_components
          ) {
            const components = place.address_components;
            const extract = (types, short = false) => {
              const comp = components.find((c) =>
                types.some((t) => c.types.includes(t))
              );
              return comp ? (short ? comp.short_name : comp.long_name) : '';
            };

            const rawRoute = extract(['route']);
            let inferredName = rawRoute;
            let inferredType = '';
            let inferredTypeAbbrev = '';

            const routeParts = rawRoute.split(' ');

            if (routeParts.length > 1) {
              const lastPart = routeParts[routeParts.length - 1].toUpperCase();

              const matchedStreetType = (streetType || []).find((item) => {
                const full = item.label?.toUpperCase();
                const abbr = item.streetAbbrev?.toUpperCase();

                return (
                  rawRoute.toUpperCase().endsWith(` ${full}`) ||
                  rawRoute.toUpperCase().endsWith(` ${abbr}`)
                );
              });

              if (matchedStreetType) {
                inferredType = matchedStreetType.label;
                inferredTypeAbbrev = matchedStreetType.streetAbbrev;

                inferredName = rawRoute
                  .replace(
                    new RegExp(`\\s${matchedStreetType.label}$`, 'i'),
                    ''
                  )
                  .replace(
                    new RegExp(`\\s${matchedStreetType.streetAbbrev}$`, 'i'),
                    ''
                  )
                  .trim();
              }
            }

            const googleUnitNo = extract(['subpremise']);
            let googleStreetNum = extract(['street_number']);
            const googleStreetName = inferredName || rawRoute;
            const googleTown = extract(['locality', 'suburb']);
            const googlePostCode = extract(['postal_code']);
            const googleState = extract(['administrative_area_level_1'], true);

            let finalUnitNo = googleUnitNo;
            let finalStreetNum = googleStreetNum;

            if (finalStreetNum.includes('/')) {
              const parts = finalStreetNum.split('/');
              finalUnitNo = parts[0].trim();
              finalStreetNum = parts[1].trim();
            } else if (
              !finalUnitNo &&
              /[A-Z]/i.test(finalStreetNum) &&
              !isNaN(parseInt(finalStreetNum))
            ) {
              const numMatch = finalStreetNum.match(/^(\d+)([A-Z]+)$/i);
              if (numMatch) {
                finalUnitNo = numMatch[2];
                finalStreetNum = numMatch[1];
              }
            }

            const latVal = place.geometry?.location
              ? place.geometry.location.lat()
              : null;
            const lngVal = place.geometry?.location
              ? place.geometry.location.lng()
              : null;

            setSelectedMarker({
              lat: latVal,
              lng: lngVal,
            });

            const freshAddress = {
              unitNo: finalUnitNo || '',
              streetNumber: finalStreetNum || '',
              streetName: googleStreetName || '',
              selectedStreetType: inferredType || '',
              selectedStreetTypeabbrev: inferredTypeAbbrev || '',
              town: googleTown || '',
              postCode: googlePostCode || '',
              selectedState: googleState,
              country: 'Australia',
              latitude: latVal,
              longitude: lngVal,
            };

            setAddressDetails(freshAddress);
            setIsVerified(true);
            setIsManuallyEdited(false);
            setTriggerAnimate(true);
            setTimeout(() => setTriggerAnimate(false), 600);

            setInputValues({
              unitNo: freshAddress.unitNo || '',
              streetNumber: freshAddress.streetNumber || '',
              streetName: freshAddress.streetName || '',
              town: freshAddress.town || '',
              postCode: freshAddress.postCode || '',
            });
            setOptions({
              unitNo: [],
              streetNumber: [],
              streetName: [],
              town: [],
              postCode: [],
            });
          }
        }
      );
    }
  };

  const handleReset = () => {
    const emptied = {
      unitNo: '',
      streetNumber: '',
      streetName: '',
      selectedStreetType: '',
      selectedStreetTypeabbrev: '',
      town: '',
      postCode: '',
      selectedState: '',
      country: 'Australia',
      latitude: null,
      longitude: null,
    };
    setAddressDetails(emptied);
    setInputValues({
      unitNo: '',
      streetNumber: '',
      streetName: '',
      town: '',
      postCode: '',
    });
    setIsVerified(false);
    setIsManuallyEdited(false);
    setIsManualEntry(false);
  };

  const handleSave = async () => {
    try {
      const coords = await validateAddress();

      const finalAddress = {
        ...addressDetails,
        latitude: coords.latitude,
        longitude: coords.longitude,
      };

      if (isneedWalkscore) {
        setAddressDetails(finalAddress);
        setWalkScoreModalOpen(true);
        return;
      }

      onSave(finalAddress);
      setIsAddressModalOpen(false);
    } catch (err) {
      if (isneedWalkscore) {
        // Intercept direct close, open walk score modal list sequence instead
        setWalkScoreModalOpen(true);
      } else {
        // Pack original address mapping properties accompanied by the explicit array references
        onSave(addressDetails);
        setIsAddressModalOpen(false); // Shut down top-level parent view
      }
    }
  };

  const handleFinalConfirmWalkScores = (selectedWalkScores) => {
    setWalkScoreModalOpen(false);

    // Pack original address mapping properties accompanied by the explicit array references
    onSave({
      ...addressDetails,
      selectedWalkScores, // Exactly 3 chosen entries inside array
    });

    setIsAddressModalOpen(false); // Shut down top-level parent view
  };

  // Increased font sizes from 16px to 18px globally for text readability
  const baseInputStyles = {
    fontFamily: 'Poppins',
    borderRadius: '12px',
    height: '56px',
    fontSize: '18px',
    backgroundColor: '#FFFFFF',
    transition: 'all 0.2s ease-in-out',
  };

  const textFieldCustomSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      height: '56px',
      backgroundColor: '#FFFFFF',
      transition: 'all 0.2s ease-in-out',
      '& fieldset': { borderColor: '#E5E7EB' },
      '&:hover fieldset': { borderColor: '#965CF0' },
      '&.Mui-focused fieldset': { borderColor: '#965CF0', borderWidth: '2px' },
    },
  };

  const disabledFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      height: '56px',
      backgroundColor: '#F9FAFB',
      '& fieldset': { borderColor: '#E5E7EB !important' },
    },
    '& .MuiOutlinedInput-input.Mui-disabled': {
      WebkitTextFillColor: '#4B5563',
      fontWeight: 500,
      fontSize: '18px',
      fontFamily: 'Poppins',
    },
  };

  const renderAutocompleteField = (label, fieldKey, placeholder, startIcon) => (
    <Grid item xs={12} sm={6} md={4}>
      {/* Increased label fonts from 14px to 16px */}
      <Typography
        sx={{
          display: 'block',
          mb: '8px',
          fontSize: '16px',
          fontWeight: '600',
          color: '#374151',
          fontFamily: 'Poppins',
        }}
      >
        {label}
      </Typography>
      <Autocomplete
        freeSolo
        disableClearable
        options={options[fieldKey] || []}
        getOptionLabel={(opt) =>
          typeof opt === 'string' ? opt : opt.description || ''
        }
        filterOptions={(x) => x}
        inputValue={inputValues[fieldKey] || ''}
        onInputChange={(e, val, reason) => {
          if (reason === 'input') handleInputChange(fieldKey, val);
        }}
        onChange={(event, newValue) =>
          handleSelectOption(event, newValue, fieldKey)
        }
        loading={loadingField === fieldKey}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            sx={{
              fontFamily: 'Poppins !important',
              fontSize: '16px',
              py: 1.5,
              px: 2,
            }}
          >
            <PinDropIcon sx={{ color: '#965CF0', mr: 1.5, fontSize: '22px' }} />
            {option.description}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            sx={textFieldCustomSx}
            InputProps={{
              ...params.InputProps,
              style: baseInputStyles,
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{ color: '#965CF0', mr: 0.5 }}
                >
                  {startIcon}
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </Grid>
  );

  const updateAddressFromMarker = () => {
    if (!selectedMarker) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      {
        location: selectedMarker,
      },

      (results, status) => {
        if (status !== 'OK') return;

        const result = results[0];

        fillAddress(result);

        setShowMapView(false);
      }
    );
  };

  const fillAddress = (result) => {
    const components = result.address_components;

    const get = (type, short = false) => {
      const comp = components.find((c) => c.types.includes(type));
      return comp ? (short ? comp.short_name : comp.long_name) : '';
    };
    const streetNumber = get('street_number');
    const route = get('route');
    const suburb = get('locality');
    const postcode = get('postal_code');
    const state = get('administrative_area_level_1', true);
    const unitNo = get('subpremise') || get('premise');

    // ----------------------------
    // Extract Street Name & Street Type
    // ----------------------------
    let streetName = route;
    let selectedStreetType = '';
    let selectedStreetTypeabbrev = '';

    const matchedStreetType = (streetType || []).find((item) => {
      const full = item.label?.toUpperCase();
      const abbr = item.streetAbbrev?.toUpperCase();

      return (
        route.toUpperCase().endsWith(` ${full}`) ||
        route.toUpperCase().endsWith(` ${abbr}`)
      );
    });

    if (matchedStreetType) {
      selectedStreetType = matchedStreetType.label;
      selectedStreetTypeabbrev = matchedStreetType.streetAbbrev;

      streetName = route
        .replace(new RegExp(`\\s${matchedStreetType.label}$`, 'i'), '')
        .replace(new RegExp(`\\s${matchedStreetType.streetAbbrev}$`, 'i'), '')
        .trim();
    }

    setAddressDetails((prev) => ({
      ...prev,
      unitNo,
      streetNumber,
      streetName,
      selectedStreetType,
      selectedStreetTypeabbrev,
      town: suburb,
      postCode: postcode,
      selectedState: state,
      latitude: selectedMarker.lat,
      longitude: selectedMarker.lng,
    }));

    setInputValues({
      unitNo,
      streetNumber,
      streetName,
      town: suburb,
      postCode: postcode,
    });

    setIsVerified(true);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xl"
      fullScreen={isMobile}
      PaperProps={{
        style: {
          borderRadius: isMobile ? '0px' : '24px',
          boxShadow: '0px 30px 60px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
          margin: isMobile ? 0 : undefined,
          maxHeight: isMobile ? '100%' : 'calc(100% - 64px)',
        },
      }}
    >
      <Box
        sx={{
          height: '6px',
          background: 'linear-gradient(90deg, #965CF0 0%, #6C2AD2 100%)',
          flexShrink: 0,
        }}
      />

      <DialogTitle
        sx={{
          m: 0,
          p: isMobile ? 2 : 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#F9FAFB',
          borderBottom: '1px solid #E5E7EB',
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          {!isMobile && (
            <Box
              sx={{
                bgcolor: '#ECE5F9',
                p: 1.5,
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#965CF0',
              }}
            >
              <MapIcon sx={{ fontSize: '26px' }} />
            </Box>
          )}
          <Box>
            <Typography
              style={{
                fontFamily: 'Poppins',
                fontWeight: 700,
                fontSize: isMobile ? '20px' : '24px',
                color: '#111827',
                letterSpacing: '-0.3px',
                lineHeight: 1.2,
              }}
            >
              Confirm Address Details
            </Typography>
            <Typography
              style={{
                fontFamily: 'Poppins',
                fontSize: isMobile ? '14px' : '16px',
                color: '#6B7280',
                marginTop: '4px',
                display: 'block',
              }}
            >
              Search and verify locations against live index pools.
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 2}>
          {!showMapView && (
            <>
              <Button
                variant="outlined"
                startIcon={<MapIcon />}
                onClick={() => setShowMapView(true)}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                }}
              >
                Pick on Map
              </Button>

              {(isVerified || isManuallyEdited) && !isMobile && (
                <Button
                  variant="text"
                  color="error"
                  size="large"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontFamily: 'Poppins',
                    fontWeight: 600,
                    fontSize: '15px',
                  }}
                >
                  Reset
                </Button>
              )}
            </>
          )}

          <IconButton
            onClick={() => {
              if (showMapView) {
                setShowMapView(false); // Close only the map view
              } else {
                onClose(); // Close the AddressModal
              }
            }}
            sx={{
              color: '#9CA3AF',
              bgcolor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              p: isMobile ? 0.8 : 1,
              '&:hover': { color: '#111827', bgcolor: '#F3F4F6' },
            }}
          >
            <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>
        </Box>
      </DialogTitle>

      {!showMapView && (
        <>
          <DialogContent
            sx={{
              p: 0,
              bgcolor: '#FFFFFF',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Grid container sx={{ flexGrow: 1, minHeight: 0 }}>
              <Grid
                item
                xs={12}
                lg={8}
                sx={{
                  p: isMobile ? 2 : 3.5,
                  borderRight: isMobile ? 'none' : '1px solid #E5E7EB',
                  animation: triggerAnimate
                    ? `${smoothPop} 0.5s ease-in-out`
                    : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Grid container spacing={isMobile ? 2 : 2.5}>
                    {renderAutocompleteField(
                      'Unit / Flat No',
                      'unitNo',
                      'e.g. Unit 4B',
                      <ApartmentIcon sx={{ fontSize: '22px' }} />
                    )}
                    {renderAutocompleteField(
                      'Street Number',
                      'streetNumber',
                      'e.g. 19',
                      <TagIcon sx={{ fontSize: '22px' }} />
                    )}
                    {renderAutocompleteField(
                      'Street Name',
                      'streetName',
                      'e.g. Woronora',
                      <SignpostIcon sx={{ fontSize: '22px' }} />
                    )}

                    {/* Street Type */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        sx={{
                          display: 'block',
                          mb: '8px',
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#374151',
                          fontFamily: 'Poppins',
                        }}
                      >
                        Street Type
                      </Typography>
                      <Autocomplete
                        disabled={!isManualEntry}
                        options={streetType || []}
                        value={
                          (streetType || []).find(
                            (s) => s.label === addressDetails.selectedStreetType
                          ) || null
                        }
                        getOptionLabel={(option) =>
                          typeof option === 'string'
                            ? option
                            : option.label || ''
                        }
                        onChange={(e, value) => {
                          setAddressDetails((prev) => ({
                            ...prev,
                            selectedStreetType: value?.label || '',
                            selectedStreetTypeabbrev: value?.streetAbbrev || '',
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            sx={
                              isManualEntry
                                ? textFieldCustomSx
                                : disabledFieldSx
                            }
                            placeholder={
                              isManualEntry
                                ? 'Select Street Type'
                                : 'Auto-populated'
                            }
                            InputProps={{
                              ...params.InputProps,
                              style: {
                                ...baseInputStyles,
                                backgroundColor: isManualEntry
                                  ? '#FFFFFF'
                                  : '#F9FAFB',
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {renderAutocompleteField(
                      'Town / Suburb',
                      'town',
                      'e.g. Sutherland',
                      <LocationCityIcon sx={{ fontSize: '22px' }} />
                    )}
                    {renderAutocompleteField(
                      'Post Code',
                      'postCode',
                      'e.g. 2232',
                      <MarkAsUnreadIcon sx={{ fontSize: '22px' }} />
                    )}

                    {/* State */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        sx={{
                          display: 'block',
                          mb: '8px',
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#374151',
                          fontFamily: 'Poppins',
                        }}
                      >
                        State
                      </Typography>
                      <Autocomplete
                        disabled={!isManualEntry}
                        options={AUSTRALIAN_STATES}
                        value={addressDetails.selectedState || ''}
                        onChange={(event, value) => {
                          setAddressDetails((prev) => ({
                            ...prev,
                            selectedState: value || '',
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={
                              isManualEntry ? 'Select State' : 'Auto-populated'
                            }
                            sx={
                              isManualEntry
                                ? textFieldCustomSx
                                : disabledFieldSx
                            }
                            InputProps={{
                              ...params.InputProps,
                              style: {
                                ...baseInputStyles,
                                backgroundColor: isManualEntry
                                  ? '#FFFFFF'
                                  : '#F9FAFB',
                              },
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <PublicIcon sx={{ fontSize: '22px' }} />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* Country */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        sx={{
                          display: 'block',
                          mb: '8px',
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#374151',
                          fontFamily: 'Poppins',
                        }}
                      >
                        Country
                      </Typography>
                      <TextField
                        fullWidth
                        value={addressDetails.country}
                        disabled
                        sx={disabledFieldSx}
                        InputProps={{
                          style: {
                            ...baseInputStyles,
                            backgroundColor: '#F9FAFB',
                          },
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{ color: '#9CA3AF' }}
                            >
                              <PublicIcon sx={{ fontSize: '22px' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Dialog
                  open={openManualInfo}
                  onClose={() => setOpenManualInfo(false)}
                  maxWidth="md"
                  fullWidth
                >
                  <DialogTitle
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontFamily: 'Poppins',
                      color: '#DC2626',
                      fontWeight: 600,
                    }}
                  >
                    <WarningIcon /> Address validation failure
                  </DialogTitle>

                  <DialogContent>
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        lineHeight: 1.8,
                        color: '#DC2626',
                        fontWeight: 500,
                      }}
                    >
                      This address couldn't be matched against Google's address
                      index. This can happen with new builds, recent
                      subdivisions, informal rural addresses, or a typo in the
                      search.
                      <br />
                      <br />
                      You can still enter it manually, but it won't be geocoded.
                      That means:
                      <ul style={{ paddingLeft: 20 }}>
                        <li>No map pin will be generated.</li>
                        <li>No distance-based search results.</li>
                        <li>
                          No automatic verification against the official GNAF
                          database.
                        </li>
                      </ul>
                      Please double-check the address before saving, as you're
                      confirming that the entered details are correct.
                    </Typography>
                  </DialogContent>

                  <DialogActions>
                    <Button
                      variant="contained"
                      onClick={() => setOpenManualInfo(false)}
                      sx={{
                        textTransform: 'none',
                        fontFamily: 'Poppins',
                      }}
                    >
                      Close
                    </Button>
                  </DialogActions>
                </Dialog>
              </Grid>

              {/* Right Column Status Tracking */}
              <Grid
                item
                xs={12}
                lg={4}
                sx={{
                  bgcolor: '#F9FAFB',
                  p: isMobile ? 2 : 3.5,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  borderTop: isMobile ? '1px solid #E5E7EB' : 'none',
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Poppins',
                      fontWeight: 700,
                      fontSize: '16px',
                      color: '#4B5563',
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                    }}
                  >
                    Live System Status
                  </Typography>

                  {!isVerified && !isManuallyEdited ? (
                    <Box
                      sx={{
                        border: '2px dashed #E5E7EB',
                        borderRadius: '16px',
                        p: 3.5,
                        textAlign: 'center',
                        bgcolor: '#FFFFFF',
                        marginTop: '10px',
                      }}
                    >
                      <PinDropIcon
                        sx={{ fontSize: '48px', color: '#D1D5DB', mb: 1.5 }}
                      />
                      <Typography
                        sx={{
                          fontFamily: 'Poppins',
                          fontSize: '16px',
                          color: '#6B7280',
                          fontWeight: 600,
                        }}
                      >
                        Awaiting Selection
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Poppins',
                          fontSize: '14px',
                          color: '#9CA3AF',
                          mt: 0.5,
                          lineHeight: 1.5,
                          display: 'block',
                        }}
                      >
                        Pick an address from any autocomplete field to view its
                        verified breakdown summary.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      {isAddressFullyValid ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            bgcolor: '#ECFDF5',
                            p: 1.8,
                            borderRadius: '12px',
                            border: '1px solid #A7F3D0',
                            marginTop: '10px',
                          }}
                        >
                          <VerifiedIcon
                            sx={{ color: '#10B981', fontSize: '24px' }}
                          />
                          <Typography
                            sx={{
                              fontFamily: 'Poppins',
                              fontWeight: 600,
                              fontSize: '16px',
                              color: '#065F46',
                            }}
                          >
                            GNAF Maps Validated
                          </Typography>
                        </Box>
                      ) : isManuallyEdited ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            bgcolor: '#FEF2F2',
                            p: 1.8,
                            borderRadius: '12px',
                            border: '1px solid #FCA5A5',
                            marginTop: '10px',
                          }}
                        >
                          <EditLocationAltIcon
                            sx={{ color: '#DC2626', fontSize: '24px' }}
                          />
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins',
                                fontWeight: 600,
                                fontSize: '16px',
                                color: '#991B1B',
                              }}
                            >
                              Manual Edits!
                            </Typography>

                            <IconButton
                              size="small"
                              onClick={() => setOpenManualInfo(true)}
                              sx={{
                                color: '#991B1B',
                              }}
                            >
                              <InfoIcon
                                style={{
                                  fontSize: '20px',
                                  color: '#d32f2f',
                                  cursor: 'pointer',
                                  marginLeft: '5px',
                                }}
                              />
                            </IconButton>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            bgcolor: '#FFF7ED',
                            p: 1.8,
                            borderRadius: '12px',
                            border: '1px solid #FFEDD5',
                          }}
                        >
                          <ErrorOutlineIcon
                            sx={{ color: '#EA580C', fontSize: '24px' }}
                          />
                          <Typography
                            sx={{
                              fontFamily: 'Poppins',
                              fontWeight: 600,
                              fontSize: '16px',
                              color: '#9A3412',
                            }}
                          >
                            Incomplete Address Data
                          </Typography>
                        </Box>
                      )}

                      <Box
                        sx={{
                          bgcolor: '#FFFFFF',
                          borderRadius: '16px',
                          p: 2.5,
                          boxShadow: '0px 4px 12px rgba(0,0,0,0.03)',
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Poppins',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#9CA3AF',
                            textTransform: 'uppercase',
                          }}
                        >
                          Full Compiled Value
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Poppins',
                            fontSize: '19px',
                            fontWeight: 700,
                            color: '#111827',
                            mt: 0.5,
                            lineHeight: 1.4,
                            display: 'block',
                          }}
                        >
                          {`${
                            addressDetails.unitNo
                              ? addressDetails.unitNo + '/'
                              : ''
                          }${
                            addressDetails.streetNumber
                              ? addressDetails.streetNumber + ' '
                              : ''
                          }${addressDetails.streetName}${
                            addressDetails.selectedStreetType
                              ? ' ' + addressDetails.selectedStreetType
                              : ''
                          }`.trim() || '—'}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Poppins',
                            fontSize: '16px',
                            fontWeight: 500,
                            color: '#4B5563',
                            mt: 0.5,
                            display: 'block',
                          }}
                        >
                          {addressDetails.town ||
                          addressDetails.selectedState ||
                          addressDetails.postCode
                            ? `${addressDetails.town}${
                                addressDetails.town &&
                                addressDetails.selectedState
                                  ? ', '
                                  : ''
                              }${addressDetails.selectedState} ${
                                addressDetails.postCode
                              }`.trim()
                            : '—'}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Poppins',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#6B7280',
                            mt: 0.5,
                            display: 'block',
                          }}
                        >
                          {addressDetails.country}
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins',
                                fontSize: '12px',
                                color: '#9CA3AF',
                                fontWeight: 600,
                              }}
                            >
                              REGION
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins',
                                fontSize: '15px',
                                fontWeight: 700,
                                color: '#374151',
                                mt: 0.5,
                                display: 'block',
                              }}
                            >
                              {addressDetails.selectedState || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins',
                                fontSize: '12px',
                                color: '#9CA3AF',
                                fontWeight: 600,
                              }}
                            >
                              POSTCODE
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins',
                                fontSize: '15px',
                                fontWeight: 700,
                                color: '#374151',
                                mt: 0.5,
                                display: 'block',
                              }}
                            >
                              {addressDetails.postCode || '-'}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Box
                          sx={{
                            mt: 2,
                            pt: 1.5,
                            borderTop: '1px dashed #E5E7EB',
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Poppins',
                              fontSize: '12px',
                              color: '#9CA3AF',
                              fontWeight: 600,
                            }}
                          >
                            LAT:{' '}
                            {addressDetails.latitude
                              ? addressDetails.latitude.toFixed(6)
                              : 'null'}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Poppins',
                              fontSize: '12px',
                              color: '#9CA3AF',
                              fontWeight: 600,
                            }}
                          >
                            LNG:{' '}
                            {addressDetails.longitude
                              ? addressDetails.longitude.toFixed(6)
                              : 'null'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
        </>
      )}
      {showMapView && (
        <Box>
          <GoogleMap
            zoom={17}
            center={
              selectedMarker
                ? selectedMarker
                : {
                    lat: addressDetails.latitude || -33.8688,

                    lng: addressDetails.longitude || 151.2093,
                  }
            }
            mapContainerStyle={{
              height: '600px',

              width: '100%',
            }}
            onClick={(e) => {
              setSelectedMarker({
                lat: e.latLng.lat(),

                lng: e.latLng.lng(),
              });
            }}
          >
            {selectedMarker && (
              <Marker
                position={selectedMarker}
                icon={getMarkerIcon()}
                draggable={true}
                onDragEnd={(e) => {
                  setSelectedMarker({
                    lat: e.latLng.lat(),

                    lng: e.latLng.lng(),
                  });
                }}
              />
            )}
          </GoogleMap>
          {showMapView && (
            <Box
              display="flex"
              justifyContent="flex-end"
              gap={2}
              p={2}
              sx={{ position: 'sticky', bottom: 0, bgcolor: '#F9FAFB' }}
            >
              <Button
                onClick={() => {
                  setShowMapView(false);
                }}
                style={{ textTransform: 'capitalize' }}
              >
                Back
              </Button>

              <Button
                variant="contained"
                onClick={updateAddressFromMarker}
                style={{ textTransform: 'capitalize' }}
              >
                Use this location
              </Button>
            </Box>
          )}
        </Box>
      )}

      {!showMapView && (
        <>
          <Divider sx={{ flexShrink: 0 }} />

          <DialogActions
            sx={{
              p: isMobile ? 2 : 2.5,
              px: isMobile ? 2 : 4,
              bgcolor: '#F9FAFB',
              display: 'flex',
              justifyContent: isMobile ? 'space-between' : 'flex-end',
              gap: 2,
              flexShrink: 0,
            }}
          >
            {isMobile && (
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={!isVerified && !isManuallyEdited}
                sx={{
                  borderRadius: '12px',
                  height: '52px',
                  fontSize: '16px',
                  fontFamily: 'Poppins',
                  textTransform: 'none',
                  borderColor: '#E5E7EB',
                  color: '#374151',
                  px: 3,
                  '&:disabled': { color: '#9CA3AF', borderColor: '#E5E7EB' },
                }}
              >
                Reset
              </Button>
            )}
            {!isManualEntry ? (
              <Button
                variant="outlined"
                onClick={handleManualEntry}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: { xs: '10px', md: '14px' },
                }}
              >
                Manual Entry
              </Button>
            ) : (
              <Button
                variant="contained"
                disabled={!isManualAddressValid}
                onClick={handleSave}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: { xs: '10px', md: '14px' },
                }}
              >
                Save Manual Address
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isAddressFullyValid}
              endIcon={<ArrowForwardIcon />}
              sx={{
                m: '0px !important',
                borderRadius: '12px !important',
                height: '54px',
                fontSize: { xs: '10px', md: '14px' },
                fontFamily: 'Poppins',
                textTransform: 'none',
                flexGrow: isMobile ? 1 : 0,
                px: isMobile ? 3 : 6,
                boxShadow: isAddressFullyValid
                  ? '0px 4px 14px rgba(150, 92, 240, 0.35)'
                  : 'none',
                backgroundColor: isAddressFullyValid ? '#965CF0' : '#E5E7EB',
                '&:hover': {
                  backgroundColor: isAddressFullyValid ? '#7C3AED' : '#E5E7EB',
                },
              }}
            >
              Save Address
            </Button>
          </DialogActions>
        </>
      )}

      {/* Append nested flow control reference overlay */}
      <WalkScoreModal
        open={walkScoreModalOpen}
        onClose={() => {
          setWalkScoreModalOpen(false);
          setIsChanged(false);
        }}
        onConfirm={handleFinalConfirmWalkScores}
        addressDetails={addressDetails}
        selectedwalkscore={initialData?.selectedWalkScores}
        isChanged={isChanged}
      />
    </Dialog>
  );
}
